import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Brain, Monitor, Grid as GridIcon, Database, Share2, Activity, Binary } from "lucide-react";

// ----- Enhanced Typewriter Component -----
const Typewriter = ({ text, speed = 50, delay = 0, className = "", start = false }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [cursorVisible, setCursorVisible] = useState(true);
  const containerRef = useRef(null);
  const hiddenRef = useRef(null);
  const [minHeight, setMinHeight] = useState(0);

  // Blink the cursor.
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, 530);
    return () => clearInterval(blinkInterval);
  }, []);

  // Measure the final text height once the hidden element is rendered.
  useEffect(() => {
    if (hiddenRef.current) {
      setMinHeight(hiddenRef.current.offsetHeight);
    }
  }, [text]);

  // Animate the text when 'start' is true.
  useEffect(() => {
    if (!start) {
      setDisplayedText("");
      return;
    }
    let animationFrame;
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime - delay;
      if (elapsed < 0) {
        animationFrame = requestAnimationFrame(animate);
        return;
      }
      const totalDuration = text.length * speed;
      if (elapsed >= totalDuration) {
        setDisplayedText(text);
        return;
      }
      const targetIndex = Math.floor(elapsed / speed);
      setDisplayedText(text.slice(0, targetIndex + 1));
      animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [text, speed, delay, start]);

  return (
    <span
      ref={containerRef}
      className={className}
      style={{ minHeight: minHeight || "auto", position: "relative" }}
    >
      {displayedText}
      <span
        className="inline-block w-[2px] h-[1em] ml-1 -mb-[0.2em] bg-current"
        style={{ opacity: cursorVisible ? 1 : 0 }}
      />
      {/* Hidden element that renders the final text to reserve space */}
      <span
        ref={hiddenRef}
        style={{
          visibility: "hidden",
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          whiteSpace: "nowrap", // if your text is not multiline
        }}
      >
        {text}
      </span>
    </span>
  );
};

const services = [
  {
    title: "AI.NEURAL.CORE",
    icon: <Brain size={32} className="text-green-400" />,
    features: [
      "Neural Network Chatbot Systems",
      "Predictive Behavioral Analytics",
      "NLP Content Enhancement",
      "AI Marketing Algorithm",
      "Process Automation Matrix",
    ],
    link: "/services/ai-neural-core",
  },
  {
    title: "WEB.QUANTUM.DEV",
    icon: <Monitor size={32} className="text-green-400" />,
    features: [
      "Dynamic User Interfaces",
      "Neural E-commerce Systems",
      "Quantum Web Applications",
      "Performance Architecture",
      "AI-Enhanced UX Protocols",
    ],
    link: "/services/web-quantum-dev",
  },
  {
    title: "DIGITAL.MATRIX.OPS",
    icon: <GridIcon size={32} className="text-green-400" />,
    features: [
      "Neural SEO Algorithms",
      "Content Generation Matrix",
      "Predictive Marketing AI",
      "Real-Time Analytics Grid",
      "Performance Tracking System",
    ],
    link: "/services/digital-matrix-ops",
  },
  {
    title: "DATA.NEXUS",
    icon: <Database size={32} className="text-green-400" />,
    features: [
      "Neural Behavior Analysis",
      "Conversion Optimization AI",
      "Advanced Analytics Matrix",
      "KPI Neural Networks",
      "Predictive Insights Engine",
    ],
    link: "/services/data-nexus",
  },
  {
    title: "SOCIAL.GRID.MATRIX",
    icon: <Share2 size={32} className="text-green-400" />,
    features: [
      "AI Content Distribution",
      "Social Pattern Analysis",
      "Influence Matrix Scanner",
      "Neural Engagement System",
      "Sentiment Analysis Core",
    ],
    link: "/services/social-grid-matrix",
  },
  {
    title: "BRAND.NEURAL.FORGE",
    icon: <Activity size={32} className="text-green-400" />,
    features: [
      "Neural Design Generation",
      "Brand Pattern Analysis",
      "Content Matrix Creation",
      "Video Enhancement Grid",
      "Multi-Neural Translation",
    ],
    link: "/services/brand-neural-forge",
  },
];

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  // Use an IntersectionObserver to trigger the typewriter effect only when in view.
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-mono font-bold mb-16 text-center">
          <Typewriter text="Service.Matrix" speed={60} start={isInView} className="inline-block" />
          <span className="block text-sm text-green-500/60 mt-2">
            <Typewriter text="// Core Systems" speed={60} delay={800} start={isInView} className="inline-block" />
          </span>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Link key={index} to={service.link} className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 transform scale-95 group-hover:scale-100 transition-transform" />
              <div className="relative border border-green-500/30 bg-gray-900/90 p-6 hover:border-green-400 transition-colors">
                <div className="w-12 h-12 mb-4 text-green-400">{service.icon}</div>
                <h3 className="text-xl font-mono font-bold mb-4">
                  <Typewriter text={service.title} speed={40} delay={index * 200} start={isInView} className="inline-block" />
                </h3>
                <ul className="space-y-2 text-green-300/80">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Binary size={16} className="mr-2 mt-1 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
